import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Typography, Flex } from "antd";
import { BulbOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { StreamingText } from "./StreamingText";
import { radius } from "@libertify/ui";

interface ThinkingProcessDisplayProps {
  texts: string[];
  isActive: boolean;
  onComplete?: () => void;
}

export const ThinkingProcessDisplay = ({
  texts,
  isActive,
  onComplete,
}: ThinkingProcessDisplayProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxRevealed, setMaxRevealed] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [sequenceDone, setSequenceDone] = useState(false);

  const handleTextComplete = useCallback(() => {
    if (currentIndex < texts.length - 1) {
      setTimeout(() => {
        const next = currentIndex + 1;
        setDirection(1);
        setCurrentIndex(next);
        setMaxRevealed(next);
      }, 1500);
    } else {
      setSequenceDone(true);
      onComplete?.();
    }
  }, [currentIndex, texts.length, onComplete]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      setDirection(-1);
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < maxRevealed) {
      setDirection(1);
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, maxRevealed]);

  const goToIndex = useCallback(
    (index: number) => {
      if (index <= maxRevealed && index !== currentIndex) {
        setDirection(index > currentIndex ? 1 : -1);
        setCurrentIndex(index);
      }
    },
    [maxRevealed, currentIndex]
  );

  if (!isActive || texts.length === 0) return null;

  const currentText = texts[currentIndex];
  const isAnimating = currentIndex === maxRevealed && !sequenceDone;
  const showNavigation = maxRevealed > 0;

  return (
    <div style={{ marginBottom: 24 }}>
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentIndex}
          custom={direction}
          variants={{
            enter: (d: number) => ({ opacity: 0, x: d * 30 }),
            center: { opacity: 1, x: 0 },
            exit: (d: number) => ({ opacity: 0, x: d * -30 }),
          }}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.4, ease: "easeInOut" }}
        >
          <Flex
            gap={16}
            align="flex-start"
            style={{
              padding: 30,
              borderRadius: radius.box,
              backgroundColor: "#F0F4FF",
              border: "1px solid #D6E4FF",
            }}
          >
            <Flex
              align="center"
              justify="center"
              style={{
                width: 40,
                height: 40,
                minWidth: 40,
                borderRadius: radius.circle,
                backgroundColor: "#ADC6FF",
              }}
            >
              <BulbOutlined style={{ fontSize: 20, color: "#fff" }} />
            </Flex>
            <Typography.Text
              style={{
                margin: 0,
                color: "#000",
                fontFamily: "Poppins, sans-serif",
                fontSize: 16,
                fontStyle: "italic",
                fontWeight: 500,
                lineHeight: "24px",
                whiteSpace: "pre-wrap",
                flex: 1,
              }}
            >
              {isAnimating ? (
                <StreamingText
                  text={currentText}
                  animate
                  wordDelay={0.2}
                  onComplete={handleTextComplete}
                />
              ) : (
                currentText
              )}
            </Typography.Text>
          </Flex>
        </motion.div>
      </AnimatePresence>

      {showNavigation && (
        <Flex
          align="center"
          justify="center"
          gap={12}
          style={{ marginTop: 12 }}
        >
          <button
            onClick={goToPrev}
            disabled={currentIndex === 0}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: radius.circle,
              border: "1px solid #D6E4FF",
              backgroundColor: currentIndex === 0 ? "#F5F5F5" : "#fff",
              cursor: currentIndex === 0 ? "not-allowed" : "pointer",
              color: currentIndex === 0 ? "#BFBFBF" : "#597EF7",
              transition: "all 0.2s",
            }}
            aria-label="Previous thought"
          >
            <LeftOutlined style={{ fontSize: 12 }} />
          </button>

          <Flex gap={6} align="center">
            {texts.slice(0, maxRevealed + 1).map((_, index) => (
              <button
                key={`dot-${texts[index]?.slice(0, 10)}-${index}`}
                onClick={() => goToIndex(index)}
                style={{
                  width: currentIndex === index ? 20 : 8,
                  height: 8,
                  borderRadius: radius.pill,
                  border: "none",
                  backgroundColor:
                    currentIndex === index ? "#597EF7" : "#D6E4FF",
                  cursor: index <= maxRevealed ? "pointer" : "default",
                  transition: "all 0.3s ease",
                  padding: 0,
                }}
                aria-label={`Go to thought ${index + 1}`}
              />
            ))}
          </Flex>

          <button
            onClick={goToNext}
            disabled={currentIndex >= maxRevealed}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: radius.circle,
              border: "1px solid #D6E4FF",
              backgroundColor: currentIndex >= maxRevealed ? "#F5F5F5" : "#fff",
              cursor: currentIndex >= maxRevealed ? "not-allowed" : "pointer",
              color: currentIndex >= maxRevealed ? "#BFBFBF" : "#597EF7",
              transition: "all 0.2s",
            }}
            aria-label="Next thought"
          >
            <RightOutlined style={{ fontSize: 12 }} />
          </button>
        </Flex>
      )}
    </div>
  );
};
